/**
 * A bounded fan-out, for providers whose list is a list of ids.
 *
 * Both providers that exist have the same shape of problem: one cheap call
 * names N things, and reading them is N more calls. Firing all of them at once
 * is what gets an account rate-limited; doing them one at a time is what makes
 * a month of mail take a minute. A small pool is neither.
 */

/**
 * Run `task` over `items`, at most `concurrency` at a time, and return what it
 * produced -- nulls dropped, **input order preserved**.
 *
 * The order matters: it is the order the provider ranked its results in, and
 * the order requests happen to finish in is not that. Results are written into
 * a pre-sized array rather than pushed, which is what keeps the two apart.
 *
 * A rejection propagates: one dead request means the list is incomplete, and
 * an incomplete list of somebody's mail is worse than the error the loader
 * turns this into.
 */
export async function mapPool<T, R>(
  items: T[],
  concurrency: number,
  task: (item: T, index: number) => Promise<R | null>,
  signal?: AbortSignal,
): Promise<R[]> {
  const results: (R | null)[] = new Array(items.length).fill(null);
  let next = 0;

  async function worker() {
    for (;;) {
      const index = next++;
      if (index >= items.length) return;
      // The in-flight fetches reject on their own when the signal fires; this
      // is only about not starting any more of them.
      if (signal?.aborted) return;
      results[index] = await task(items[index], index);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, worker),
  );
  return results.filter((result): result is R => result !== null);
}
